import { useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAsync } from '../lib/useAsync'
import { formatDate, formatPeriod } from '../lib/format'
import { useSelectedYear } from '../year/useYear'
import type { DocKind, Unit } from '../lib/types'
import { Loading, ErrorState, EmptyState } from '../components/States'
import { SelectField } from '../components/Fields'
import { DocumentViewer } from '../components/DocumentViewer'

type DocKindKey =
  | 'doc_kind_contrat' | 'doc_kind_quittance' | 'doc_kind_recu'
  | 'doc_kind_facture' | 'doc_kind_bordereau' | 'doc_kind_etat_des_lieux'
  | 'doc_kind_piece_identite' | 'doc_kind_photo' | 'doc_kind_autre'

const KINDS: DocKind[] = [
  'recu', 'facture', 'contrat', 'bordereau', 'quittance',
  'etat_des_lieux', 'piece_identite', 'photo', 'autre',
]

interface DocRow {
  id: string
  kind: DocKind
  label: string | null
  period: string | null
  uploaded_at: string
  byte_size: number | null
  mime_type: string | null
  unit_id: string | null
  tenant_id: string | null
  unit: { name: string } | null
  tenant: { full_name: string } | null
}

/**
 * Pieces de l'annee consultee.
 *
 * Une piece est retenue si elle porte un mois de l'annee, OU si elle a
 * ete envoyee pendant l'annee. Le second critere rattrape les pieces
 * sans mois : un contrat, une piece d'identite, un etat des lieux ne
 * se rapportent a aucun mois mais bien a une annee.
 */
async function load(year: number) {
  const debut = `${year}-01-01`
  const finExclue = `${year + 1}-01-01`

  const { data, error } = await supabase
    .from('documents')
    .select(
      'id, kind, label, period, uploaded_at, byte_size, mime_type,' +
        ' unit_id, tenant_id, unit:units(name), tenant:tenants(full_name)',
    )
    .or(
      `period.like.${year}-%,` +
        `and(period.is.null,uploaded_at.gte.${debut},uploaded_at.lt.${finExclue})`,
    )
    .order('uploaded_at', { ascending: false })
    .limit(500)
  if (error) throw error

  const { data: units } = await supabase
    .from('units')
    .select('id, name, kind, floor, notes, vacant_since, vacancy_reason')
    .order('name')

  const { data: tenants } = await supabase
    .from('tenants')
    .select('id, full_name')
    .order('full_name')

  return {
    docs: (data ?? []) as unknown as DocRow[],
    units: (units as Unit[]) ?? [],
    tenants: (tenants ?? []) as { id: string; full_name: string }[],
  }
}

/**
 * Toutes les pieces justificatives, filtrables par type, unite,
 * locataire et mois. C'est ici qu'on retrouve un recu six mois plus
 * tard sans fouiller WhatsApp.
 */
export function DocumentsScreen({ onBack }: { onBack: () => void }) {
  const { t, lang } = useI18n()
  const { year } = useSelectedYear()
  const { data, loading, error, reload } = useAsync(() => load(year), [year])

  const [kind, setKind] = useState<string>('')
  const [unitId, setUnitId] = useState<string>('')
  const [tenantId, setTenantId] = useState<string>('')
  const [period, setPeriod] = useState<string>('')
  const [open, setOpen] = useState<string | null>(null)

  const docs = data?.docs ?? []

  // Les mois reellement presents, plutot qu'une liste figee.
  const periods = useMemo(() => {
    const set = new Set<string>()
    for (const d of docs) if (d.period) set.add(d.period)
    return [...set].sort().reverse()
  }, [docs])

  const filtres = docs.filter((d) => {
    if (kind && d.kind !== kind) return false
    if (unitId && d.unit_id !== unitId) return false
    if (tenantId && d.tenant_id !== tenantId) return false
    if (period && d.period !== period) return false
    return true
  })

  return (
    <div className="screen">
      <button type="button" className="backlink" onClick={onBack}>
        ← {t('back')}
      </button>

      <h1 className="screen__title">{t('documents_title')}</h1>

      {loading && <Loading />}
      {error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (
        <>
          <div className="filters">
            <SelectField
              label={t('doc_kind')}
              value={kind}
              onChange={setKind}
              options={[
                { value: '', label: t('filter_all') },
                ...KINDS.map((k) => ({
                  value: k,
                  label: t(`doc_kind_${k}` as DocKindKey),
                })),
              ]}
            />
            <SelectField
              label={t('lease_unit')}
              value={unitId}
              onChange={setUnitId}
              options={[
                { value: '', label: t('filter_all') },
                ...(data?.units ?? []).map((u) => ({ value: u.id, label: u.name })),
              ]}
            />
            <SelectField
              label={t('lease_tenant')}
              value={tenantId}
              onChange={setTenantId}
              options={[
                { value: '', label: t('filter_all') },
                ...(data?.tenants ?? []).map((tn) => ({
                  value: tn.id,
                  label: tn.full_name,
                })),
              ]}
            />
            <SelectField
              label={t('report_period')}
              value={period}
              onChange={setPeriod}
              options={[
                { value: '', label: t('filter_all') },
                ...periods.map((p) => ({ value: p, label: formatPeriod(p, lang) })),
              ]}
            />
          </div>

          {filtres.length === 0 ? (
            <EmptyState title={t('documents_empty')} />
          ) : (
            <>
              <p className="muted">{t('report_rows', { n: filtres.length })}</p>
              <ul className="list">
                {filtres.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      className="list__item list__item--tappable"
                      onClick={() => setOpen(d.id)}
                    >
                      <span className="list__main">
                        <span className="list__name">
                          {t(`doc_kind_${d.kind}` as DocKindKey)}
                        </span>
                        <span className="list__meta">
                          {[
                            d.unit?.name,
                            d.tenant?.full_name,
                            d.period ? formatPeriod(d.period, lang) : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                        <span className="list__note">
                          {formatDate(d.uploaded_at.slice(0, 10))}
                          {d.byte_size
                            ? ` · ${Math.max(1, Math.round(d.byte_size / 1024))} Ko`
                            : ''}
                        </span>
                      </span>
                      <span className="list__side">
                        <span className="chip chip--ok">{t('doc_view')}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {open && (
        <DocumentViewer documentId={open} onClose={() => setOpen(null)} />
      )}
    </div>
  )
}
